/***
 * Created on 2023/9/6
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import {getLocalUser} from "./user";
import {ErrorCode} from "./error_code";

export const GLOBAL = 'GLOBAL';
export const CHINA = 'CHINA';

export const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,63}$/;
export const phonePattern = /^\d{6,16}$/;


export const getCurrentDomainRegion = () => {
    if (window.location.hostname?.toLocaleLowerCase().endsWith('cn')) {
        return CHINA
    }
    return GLOBAL;
}

export const detectDefaultLanguage = () => {
    const search = new URLSearchParams(location.search);
    const backup_lang = getCurrentDomainRegion() === CHINA ? 'zh' : 'en';
    const browser_lang = navigator.language || navigator.userLanguage;
    const currentUser = getLocalUser();
    const user_lang = currentUser?.language || null;
    const current_lang = getLocalLanguage();
    const lang = search.get('lang') ||current_lang || user_lang || browser_lang || backup_lang;
    if (lang.toLowerCase() === 'en-us') {
        return 'en';
    }
    if (['zh_tw', 'zh-tw'].includes(lang.toLowerCase())) {
        return 'zh_TW';
    }
    return Object.values(SUPPORTED_LANGUAGES).find(item => item.value === lang) ? lang : 'en';
}

export const getLocalLanguage = () => {
    if ( getCurrentDomainRegion() === CHINA) {
        return 'zh';
    }
    const lang = localStorage.getItem('currentLang');
    return lang;
}

export const setLocalLanguage = (lang) => {
    if (lang.toLowerCase() === 'en-us') {
        lang = 'en';
    }
    localStorage.setItem('currentLang', lang);
}

export const SUPPORTED_LANGUAGES = [
    { value: "en", "name": "English", "en_name": "English"},
    { value: "zh_TW", "name": "繁體中文", "en_name": "Traditional Chinese"},
    { value: "zh", "name": "简体中文", "en_name": "Simplified Chinese"},
    { value: "cs", "name": "Czech", "en_name": "Czech"},
    { value: "da", "name": "Dansk", "en_name": "Danish"},
    { value: "de", "name": "Deutsch", "en_name": "German"},
    { value: "es", "name": "Español", "en_name": "Spanish"},
    { value: "fr", "name": "Français", "en_name": "French"},
    { value: "it", "name": "Italiano", "en_name": "Italian"},
    { value: "ja", "name": "日本語", "en_name": "Japanese"},
    { value: "ko", "name": "한국", "en_name": "Korean"},
    { value: "no", "name": "Norsk", "en_name": "Norwegian"},
    { value: "pl", "name": "Polski", "en_name": "Polish"},
    { value: "ru", "name": "Русский", "en_name": "Russian"},
    { value: "fi", "name": "Suomi", "en_name": "Finnish"},
    { value: "sv", "name": "Svenska", "en_name": "Swedish"},
    { value: "nl", "name": "Nederlands", "en_name": "Dutch"},
    { value: "tr", "name": "Turk dili", "en_name": "Turkish"},
    { value: "th", "name": "ไทย", "en_name": "Thai"},
    { value: "pt", "name": "Português", "en_name": "Portuguese"},
    { value: "hu", "name": "Magyar", "en_name": "Hungarian"},
    { value: "el", "name": "Ελληνικά", "en_name": "Greek"},
    { value: "ro", "name": "Român", "en_name": "Romanian"}
]

export const convertToQNAPLang = (lang) => {
    switch (lang) {
        case 'zh_TW':
            return 'zh-tw';
        case 'zh':
            return 'zh-cn';
        case 'de':
            return 'de-de';
        case 'es':
            return 'es-es';
        case 'fr':
            return 'fr-fr'
        case 'it':
            return 'it-it'
        case 'nl':
            return 'nl-nl'
        case 'sv':
            return 'sv-se'
        case 'pt':
            return 'pt-pt'
        case 'pl':
            return 'pl-pl'
        case 'ja':
            return 'ja-jp'
        case 'ko':
            return 'ko-kr'
        case 'cs':
            return 'cs-cz'
        case 'th':
            return 'th-th'
        case 'tr':
            return 'tr-tr'
        case 'hu':
            return 'hu-hu'
        case 'da':
            return 'da-dk'
        default:
            return 'en';
    }
}

export const getAppCDNUrl = () => {
    return getCurrentDomainRegion() === GLOBAL ? window.acc_mf.common.APP_CDN_URL : window.acc_mf.common.APP_CDN_URL_CN;
}

export const getAppCMSUrl = () => {
    return getCurrentDomainRegion() === GLOBAL ? window.acc_mf.common.APP_CMS_URL : window.acc_mf.common.APP_CMS_URL_CHINA;
}

export const getServiceStatusUrl = () => {
    return getCurrentDomainRegion() === GLOBAL ? window.acc_mf.common.APP_SERVICES_STATUS : window.acc_mf.common.APP_SERVICES_STATUS_CHINA;
}

export const getQNAPUrl = () => {
    return window.acc_mf.common.APP_QNAP_URL;
}

export const getPrivacyPolicyUrl = () => {
    return getCurrentDomainRegion() === GLOBAL ? window.acc_mf.common.APP_PRIVACY_POLICY_GLOBAL : window.acc_mf.common.APP_PRIVACY_POLICY_CHINA;
}

export const getMyQnapCloudUrl = () => {
    return getCurrentDomainRegion() === GLOBAL ? window.acc_mf.common.MYQNAPCLOUD_URL : window.acc_mf.common.MYQNAPCLOUD_URL_CN;
}

export const getErrorMessage = (t, key, options={}) => {
    const msg_key = key ? `${key}` : 'default_error';
    let message = ErrorCode?.[msg_key] || ErrorCode.default_error;
    let interpolation;
    if (options?.prefix && options?.suffix) {
        interpolation = {
            prefix: options.prefix,
            suffix: options.suffix,
            escapeValue: false
        }
    }
    if (interpolation) {
        return t(message, {...options.params, interpolation});
    }
    return t(message);
}

export const handleInputValidationError = (t, input, validation_type="") => {
    const state = input?.validity;
    if (state && !state.valid) {
        if (state.valueMissing) {
            return getErrorMessage(t, "field_required")
        }
        else if (state.patternMismatch) {
            if (validation_type === 'email') {
                return getErrorMessage(t, "1000003")
            }
            else if (validation_type === 'password') {
                return getErrorMessage(t, "1050005")
            }
            else if (validation_type === 'qid') {
                return getErrorMessage(t, "qid_format_error")
            }
            else {
                return getErrorMessage(t, "invalid_format")
            }
        }
        return getErrorMessage(t, "invalid_format")
    }
}

export const openFixedWindow = (url, callback, {width=640, height=480}) => {
    const y = window.top.outerHeight / 2 + window.top.screenY - ( height / 2);
    const x = window.top.outerWidth / 2 + window.top.screenX - ( width / 2);
    const popup = window.open(url, 'Social Signin Window', `toolbar=no, location=no, directories=no, status=no, menubar=no, scrollbars=no, resizable=no, copyhistory=no, width=${width}, height=${height}, top=${y}, left=${x}`);
    const interval_id = setInterval(async () => {
        await callback(interval_id, popup);
    }, 500);
    // popup.addEventListener('beforeunload', () => {
    //     clearInterval(interval_id);
    // })
    return popup;
}

export class SocialPlatformService  {
    static getPlatformName(platform) {
        const platform_map = {
            google: 'Google',
            facebook: 'Facebook',
            appleid: 'Apple Id',
            weixin: 'WeiXin'
        }
        return platform_map[platform];
    }

    static generateGoogleSignInUrl() {
        const url = new URL(window.acc_mf.auth.GOOGLE.SERVER_URL);
        const randomString = [...Array(32)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
        sessionStorage.setItem('goauth_state', randomString);
        url.searchParams.append('response_type', 'code');
        url.searchParams.append('client_id', window.acc_mf.auth.GOOGLE.CLIENT_ID);
        url.searchParams.append('scope', 'openid email profile');
        url.searchParams.append('redirect_uri', window.acc_mf.auth.GOOGLE.REDIRECT_URI);
        url.searchParams.append('state', randomString);
        url.searchParams.append('prompt', 'select_account');
        return url.toString();
    }

    static generateFacebookSigninUrl() {
        const url = new URL(window.acc_mf.auth.FACEBOOK.SERVER_URL);
        const randomString = [...Array(32)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
        sessionStorage.setItem('fb_oauth_state', randomString);
        url.searchParams.set('client_id', window.acc_mf.auth.FACEBOOK.CLIENT_ID);
        url.searchParams.set('redirect_uri', window.acc_mf.auth.FACEBOOK.REDIRECT_URI);
        url.searchParams.set('scope', 'email,public_profile');
        url.searchParams.set('response_type', 'code');
        url.searchParams.set('state', randomString);
        return url.toString();
    }

    static generateAppleIdSignInUrl() {
        const url = new URL(window.acc_mf.auth.APPLEID.OAUTH_AUTH_URL);
        const randomString = [...Array(32)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
        sessionStorage.setItem('apple_oauth_state', randomString);
        url.searchParams.append('response_type', 'code');
        url.searchParams.append('response_mode', 'form_post');
        url.searchParams.append('client_id', window.acc_mf.auth.APPLEID.CLIENT_ID);
        url.searchParams.append('redirect_uri', window.acc_mf.auth.APPLEID.REDIRECT_URI);
        url.searchParams.append('scope', window.acc_mf.auth.APPLEID.SCOPE);
        url.searchParams.append('state', randomString);
        return url.toString();
    }

    static generateWeixinSignInUrl() {
        const randomString = [...Array(32)].map(() => (~~(Math.random() * 36)).toString(36)).join('');
        sessionStorage.setItem('woauth_state', randomString);
        const url = new URL(window.acc_mf.auth.WEIXIN.QRCODE_URL);
        url.searchParams.append('appid', window.acc_mf.auth.WEIXIN.APP_ID);
        url.searchParams.append('redirect_uri', `${window.acc_mf.auth.WEIXIN.REDIRECT_URI}`)
        url.searchParams.append('response_type', 'code');
        url.searchParams.append('scope', 'snsapi_login');
        url.searchParams.append('state', randomString);
        return url.toString();
    }

    getPlatformName(platform) {
        return SocialPlatformService.getPlatformName(platform);
    }

    async linkPlatform(platform, linkAction, errorHandler) {
        if (platform === 'google') {
            const url = SocialPlatformService.generateGoogleSignInUrl();
            console.log(url);
            openFixedWindow(url.toString(), async (interval_id, popup) => {
                try {
                    const return_url = new URL(popup.location.href);
                    if (return_url.pathname === '/oauth2callback') {
                        const state = return_url.searchParams.get('state');
                        const isValid = sessionStorage.getItem('goauth_state') === state;
                        sessionStorage.removeItem('goauth_state');
                        if (!isValid) {
                            popup.close();
                            clearInterval(interval_id);
                            // store.globalAlert.setAlert(t('@Fail to verify your account with Google. Please sign up with other way.@'), 'error', true);
                            return;
                        }
                        // handle the code here.
                        const data = {
                            platform: 'google',
                            social_token: return_url.searchParams.get('code'),
                            action: 'signin',
                            is_mobile: false
                        };

                        await linkAction(data)
                        popup.close();
                        clearInterval(interval_id);
                    }
                } catch (err) {
                    console.log(err)
                    if (typeof errorHandler === 'function') {
                        errorHandler(err);
                    }
                }
            }, {});
        }
        else if (platform === 'facebook') {
            const url = SocialPlatformService.generateFacebookSigninUrl();
            const popup = openFixedWindow(url, async (interval_id, popup) => {
                const return_url = new URL(popup.window.location.href);
                if (return_url.pathname === '/facebook_oauth2callback') {
                    const state = return_url.searchParams.get('state');
                    const isValid = sessionStorage.getItem('fb_oauth_state') === state;
                    sessionStorage.removeItem('fb_oauth_state');
                    if (!isValid) {
                        popup.close();
                        clearInterval(interval_id);
                        // store.globalAlert.setAlert(t('@Your Facebook token is invalid, please sign up with Facebook again.@'), 'error', true);
                        return;
                    }
                    // handle the code here.
                    const data = {
                        platform: 'facebook',
                        social_token: return_url.searchParams.get('code'),
                        action: 'signin',
                        is_mobile: false
                    }

                    await linkAction(data)
                    popup.close();
                    clearInterval(interval_id);
                }
            }, {})
        }
        else if (platform === 'appleid') {
            const url = SocialPlatformService.generateAppleIdSignInUrl();
            openFixedWindow(url.toString(), async (interval_id, popup) => {
                try {
                    const return_url = new URL(popup.window.location.href);
                    if (return_url.pathname === '/appleoauth2callback') {
                        const state = return_url.searchParams.get('state');
                        const isValid = sessionStorage.getItem('apple_oauth_state') === state;
                        sessionStorage.removeItem('apple_oauth_state');
                        if (!isValid) {
                            popup.close();
                            clearInterval(interval_id);
                            // store.globalAlert.setAlert(t('@Appleid Auth Code Revoked, try login again.@'), 'error', true);
                            return;
                        }
                        // handle the code here.
                        const data = {
                            platform: 'appleid',
                            social_token: return_url.searchParams.get('code'),
                            action: 'signin',
                            is_mobile: false
                        }
                        await linkAction(data)
                        popup.close();
                        clearInterval(interval_id);
                    }
                } catch (err) {
                    console.log(err);
                }
            }, {});
        }
        else if (platform === 'weixin') {
            const url = SocialPlatformService.generateWeixinSignInUrl();
            openFixedWindow(url.toString(), async (interval_id, popup) => {
                try {
                    const return_url = new URL(popup.window.location.href);
                    if (return_url.pathname === '/oauth2callback') {
                        const state = return_url.searchParams.get('state');
                        const isValid = sessionStorage.getItem('woauth_state') === state;
                        sessionStorage.removeItem('woauth_state');
                        if (!isValid) {
                            popup.close();
                            clearInterval(interval_id);
                            store.globalAlert.setAlert(t('@Error@'), 'error', true);
                            return;
                        }
                        // handle the code here.
                        const data = {
                            platform: 'weixin',
                            social_token: return_url.searchParams.get('code'),
                            action: 'signin',
                            is_mobile: false
                        }
                        await linkAction(data)
                        popup.close();
                        clearInterval(interval_id);
                    }
                } catch (err) {
                    console.log(err);
                }
            }, {});
        }
    }

    async unLinkPlatform(platform, unlinkAction) {
        await unlinkAction({platform})
    }
}

export function getQidType(qid) {
    const reg_mail = new RegExp(emailPattern, 'gm');
    const reg_phone = new RegExp(phonePattern, 'gm');

    if (reg_mail.exec(qid)) {
        return 'email';
    }
    else if (reg_phone.exec(qid)) {
        return 'number'
    }
    return undefined;
}

export class URLHandler {
    url;

    constructor(url) {
        this.url = new URL(url);
    }

    // get url() {
    //     return this.url;
    // }

    get state() {
        return this.url?.searchParams.get('state');
    }

    get language() {
        return this.url?.searchParams.get('lang');
    }

    get clientId() {
        let client_id;
        const state = this.state;
        if (state) {
            const sub_params = state.split('?')?.[1];
            if (sub_params) {
                const parsed_sub_params = new URLSearchParams(sub_params);
                client_id = parsed_sub_params.get('client_id');
            }
        }
        else {
            client_id = this.url?.searchParams.get('app_id') || this.url?.searchParams.get('client_id')
        }
        return client_id;
    }

    get redirectURI() {
        let redirect_uri;
        const state = this.state;
        if (state) {
            const sub_params = state.split('?')?.[1];
            if (sub_params) {
                const parsed_sub_params = new URLSearchParams(sub_params);
                redirect_uri = parsed_sub_params.get('redirect_uri');
            }
        }
        return redirect_uri;
    }

    get qid() {
        const qid = this.url?.searchParams.get('qid');
        const qid_type = getQidType(qid);
        if (!qid_type) {
            return '';
        }
        else return qid;
    }

    isValidPath(path) {
        const regex = /^(?!\/\/)(\/[^:\/?#]*)*\/?$/;
        return regex.test(path);
    }

    convertQueryToNext() {
        return encodeURIComponent(this.url?.searchParams.toString());
    }

    isHideSignUp() {
        return this.url?.searchParams.get('hide_signup')?.toLowerCase() === 'true';
    }

    isHideSocial() {
        return this.url?.searchParams.get('hide_social')?.toLowerCase() === 'true';
    }

    isHideActivationMail() {
        return this.url?.searchParams.get('hide_activation_mail')?.toLowerCase() === 'true';
    }

    getSAMLSSOId() {
        return this.url?.searchParams.get('saml_sso_id');
    }

    isHideSSO() {
        return this.url?.searchParams.get('hide_sso')?.toLowerCase() === 'true';
    }

}
